// ============================================================================
// LEARNING CHROME — the pieces both Learning screens share.
//
// The band, the two pills that carry the honest numbers, and the one component that decides what
// a screen says when its endpoint does not exist yet.
// ============================================================================

import type { ReactNode } from 'react'
import type { LearningPace } from '../../lib/data'

import { HeaderBand } from '../HeaderBand'
import { ArrowDown, ArrowUp } from './icons'

/** The band, wearing Learning's own teal/green gradient. */
export function LearningBand({ children }: { children: ReactNode }) {
  return <HeaderBand band="learning">{children}</HeaderBand>
}

/**
 * A screen whose data never arrived must SAY which version brings it. An empty curriculum is a
 * claim — "this plan has no units in it" — and remote.ts throws a named error carrying the
 * sentence instead, so this prints it.
 */
export function Unavailable({ error }: { error: unknown }) {
  if (!error) return null
  return (
    <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
      <p className="max-w-[620px] text-body text-ink-3 text-pretty">
        {error instanceof Error ? error.message : 'This could not be loaded.'}
      </p>
    </div>
  )
}

/**
 * THE PROGRESS PILL — `25% · 46 of 182`, teal on `teal-fill`.
 *
 * A COUNT, NOT A RING (docs/design/FIDELITY.md, ratified deviation). The percentage alone is the
 * kind of number that feels like progress without being checkable; the count next to it is what
 * makes it auditable, and it is the count the curriculum below adds up to.
 */
export function ProgressPill({ percent, done, total }: { percent: number; done: number; total: number }) {
  return (
    <span className="flex-none whitespace-nowrap rounded-pill bg-teal-fill px-[9px] py-[3px] text-pill font-semibold text-teal">
      {percent}% · {done} of {total}
    </span>
  )
}

/**
 * THE PACE PILL — '2 days ahead' / '4 days behind' / 'on the plan line'.
 *
 * Plain words, and a direction as well as a colour: green-versus-red alone would leave the number
 * unreadable to a colour-blind reader and to a black-and-white screenshot. The phrase itself comes
 * from the data contract — this component chooses no wording.
 */
export function PacePill({ pace }: { pace: LearningPace }) {
  if (pace.direction === 'on_plan') {
    return (
      <span className="flex-none whitespace-nowrap rounded-pill bg-fill-2 px-[9px] py-[3px] text-pill font-medium text-ink-3">
        {pace.phrase}
      </span>
    )
  }
  const ahead = pace.direction === 'ahead'
  const Arrow = ahead ? ArrowUp : ArrowDown
  return (
    <span
      className={[
        'flex flex-none items-center gap-[5px] whitespace-nowrap rounded-pill px-[9px] py-[3px] text-pill font-semibold',
        ahead ? 'bg-success-fill text-success' : 'bg-danger-fill text-danger-2',
      ].join(' ')}
    >
      <Arrow size={10} className={ahead ? 'text-success-dot' : 'text-danger-dot'} />
      {pace.phrase}
    </span>
  )
}

/** A quiet pill: the unit name, the activity line. Wears whichever hue its track carries. */
export function QuietPill({
  children,
  fill = 'bg-fill-2',
  text = 'text-ink-3',
  icon,
  strong,
}: {
  children: ReactNode
  fill?: string
  text?: string
  icon?: ReactNode
  strong?: boolean
}) {
  return (
    <span
      className={[
        'flex flex-none items-center gap-[5px] whitespace-nowrap rounded-pill px-[9px] py-[3px] text-pill',
        strong ? 'font-semibold' : 'font-medium',
        fill,
        text,
      ].join(' ')}
    >
      {icon}
      {children}
    </span>
  )
}

/**
 * A band button whose behaviour arrives with a later version. Disabled and SAYING SO in its title
 * rather than looking live and doing nothing — a control that shrugs is worse than one that
 * explains itself.
 */
export function PendingButton({
  children,
  title,
  icon,
  variant = 'tile',
}: {
  children: ReactNode
  title: string
  icon?: ReactNode
  /**
   * `pill` is the overview's quiet capsule (radius 20, 500 11px, `ink-3`); `tile` and `dark` are
   * the track page's pair of real-looking actions (radius 10, 600 11.5px). Three shapes because
   * the two screens genuinely use three, not because a button needs options.
   */
  variant?: 'pill' | 'tile' | 'dark'
}) {
  const shape = {
    pill: 'rounded-pill border border-line bg-surface px-[11px] py-[6px] text-micro font-medium text-ink-3',
    tile: 'rounded-tile border border-line-2 bg-surface px-[13px] py-[7px] text-[11.5px] font-semibold text-ink-2',
    dark: 'rounded-tile bg-ink px-[13px] py-[7px] text-[11.5px] font-semibold text-surface',
  }[variant]

  return (
    <button
      type="button"
      disabled
      title={title}
      className={[
        'flex flex-none cursor-not-allowed items-center gap-[7px] whitespace-nowrap leading-none',
        shape,
      ].join(' ')}
    >
      {icon}
      {children}
    </button>
  )
}
